import { addDays, daysBetween, matchesSearch } from '@felix/domain';
import {
  CERTIFICATE_CATEGORIES,
  type CertificateCan,
  type CertificateList,
  type CertificateListItem,
  type CertificateType,
  type CertificateTypeRef,
  type ClassDueRow,
  SURVEY_KINDS,
  type SurveyKind,
  type VesselSurvey,
} from './types.js';

/**
 * Vessel Certificates ekranının saf yardımcıları (plan M7 Task 27a): ağaç, süzgeçler, özetler,
 * sıra, adres sorgusu ve alt panellerin satırları. Sayılar ve durumlar koddan (ADR 0024 #19);
 * durum sunucunun verdiğidir (eşik şirket ayarı, K30).
 */

/* ---------- Kategori rozeti ---------- */

/**
 * "Type" sütununun renkli rozeti (`gemi-sertifika-takibi.jpeg`): SOLAS, MARPOL, MLC, ISM, ISPS,
 * Environmental, Flag, Other; renkler `cert-*` token'ları (README "Tasarım değerleri"). Klas
 * sertifikasının rozeti referansta yoktur: `class` (bilgi tonu; ADR 0029 Ek: M7d ekranları E1).
 */
export const CERTIFICATE_BADGES = [
  'solas',
  'marpol',
  'mlc',
  'ism',
  'isps',
  'environmental',
  'flag',
  'class',
  'other',
] as const;
export type CertificateBadge = (typeof CERTIFICATE_BADGES)[number];

/** Türün koduna göre rozet: varsayılan ağacın türleri (ADR 0029 D2). */
const BADGE_BY_CODE: Readonly<Record<string, CertificateBadge>> = {
  'STAT-MLC': 'mlc',
  'SAFETY-ISM': 'ism',
  'SAFETY-SMS': 'ism',
  'OTHER-DOC': 'ism',
  'SAFETY-ISPS': 'isps',
  'SAFETY-SE': 'solas',
  // IOPP MARPOL Ek I'in, çöp yönetimi Ek V'in belgesidir (referansta MARPOL rozeti)
  'ENV-IOPP': 'marpol',
  'ENV-GARBAGE': 'marpol',
};

const BADGE_BY_CATEGORY: Readonly<Record<string, CertificateBadge>> = {
  statutory: 'other',
  class: 'class',
  safety: 'ism',
  environmental: 'environmental',
  flag_port: 'flag',
  other: 'other',
};

export function certificateBadge(type: CertificateTypeRef | null): CertificateBadge {
  if (!type) return 'other';
  const group = type.subCategory?.trim().toUpperCase();
  if (group === 'SOLAS') return 'solas';
  if (group === 'MARPOL') return 'marpol';
  const byCode = BADGE_BY_CODE[type.code.trim().toUpperCase()];
  if (byCode) return byCode;
  return BADGE_BY_CATEGORY[type.category] ?? 'other';
}

/* ---------- Ağaç ---------- */

export interface CertTreeNode {
  id: string;
  parentId: string | null;
  kind: 'root' | 'category' | 'group' | 'type';
  category: string | null;
  /** Ara düğüm ve tür adı; kök ve kategori adı dilden. */
  label: string | null;
  /** Alt ağaçtaki türler. */
  typeIds: string[];
  /** Alt ağaçtaki geçmişte kalmamış sertifikalar (K28). */
  count: number;
}

export const ROOT_NODE = 'all';

/**
 * "Certificate Tracking" ağacı: kök, altı kategori (boş olsa da; referans ağaç), SOLAS ve MARPOL
 * gibi ara düğümler ve türler. Sıra türlerin `sortOrder`'ıdır; ara düğüm ilk türünün yerindedir.
 */
export function certificateTree(
  types: readonly CertificateType[],
  items: readonly CertificateListItem[],
): CertTreeNode[] {
  const counts = new Map<string, number>();
  for (const item of items) {
    if (!item.superseded) counts.set(item.typeId, (counts.get(item.typeId) ?? 0) + 1);
  }
  const sorted = [...types].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.code.localeCompare(b.code, 'en'),
  );
  const nodes: CertTreeNode[] = [];
  const root: CertTreeNode = {
    id: ROOT_NODE,
    parentId: null,
    kind: 'root',
    category: null,
    label: null,
    typeIds: [],
    count: 0,
  };
  nodes.push(root);
  const categories = [
    ...CERTIFICATE_CATEGORIES,
    ...new Set(
      sorted
        .map((t) => t.category)
        .filter((c) => !(CERTIFICATE_CATEGORIES as readonly string[]).includes(c)),
    ),
  ];
  for (const category of categories) {
    const categoryNode: CertTreeNode = {
      id: `category:${category}`,
      parentId: ROOT_NODE,
      kind: 'category',
      category,
      label: null,
      typeIds: [],
      count: 0,
    };
    nodes.push(categoryNode);
    const groups = new Map<string, CertTreeNode>();
    const children: CertTreeNode[] = [];
    for (const type of sorted.filter((t) => t.category === category)) {
      const count = counts.get(type.id) ?? 0;
      const group = type.subCategory?.trim() || null;
      let parent = categoryNode;
      if (group) {
        let groupNode = groups.get(group);
        if (!groupNode) {
          groupNode = {
            id: `group:${category}:${group}`,
            parentId: categoryNode.id,
            kind: 'group',
            category,
            label: group,
            typeIds: [],
            count: 0,
          };
          groups.set(group, groupNode);
          children.push(groupNode);
        }
        groupNode.typeIds.push(type.id);
        groupNode.count += count;
        parent = groupNode;
      }
      const typeNode: CertTreeNode = {
        id: `type:${type.id}`,
        parentId: parent.id,
        kind: 'type',
        category,
        label: type.name,
        typeIds: [type.id],
        count,
      };
      children.push(typeNode);
      categoryNode.typeIds.push(type.id);
      categoryNode.count += count;
      root.typeIds.push(type.id);
      root.count += count;
    }
    // Ara düğümün türleri hemen ardından (düz liste, ağaç sırası)
    const ordered: CertTreeNode[] = [];
    for (const child of children) {
      if (child.kind === 'type' && child.parentId !== categoryNode.id) continue;
      ordered.push(child);
      if (child.kind === 'group') {
        ordered.push(...children.filter((c) => c.parentId === child.id));
      }
    }
    nodes.push(...ordered);
  }
  // Ağaçta türü olmayan sertifika da kökün sayısına girer (tür silinmiş olabilir)
  const known = new Set(root.typeIds);
  for (const item of items) {
    if (!item.superseded && !known.has(item.typeId)) root.count += 1;
  }
  return nodes;
}

/** Düğümün alt ağacındaki türler; kökte `null` (bütün sertifikalar, türü bilinmeyenler dahil). */
export function typeIdsOf(nodes: readonly CertTreeNode[], id: string | null): Set<string> | null {
  if (id === null || id === ROOT_NODE) return null;
  const node = nodes.find((n) => n.id === id);
  return node ? new Set(node.typeIds) : null;
}

/** Kökten düğüme kadar yol (breadcrumb ve liste başlığı). */
export function nodePath(nodes: readonly CertTreeNode[], id: string): CertTreeNode[] {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const path: CertTreeNode[] = [];
  let cursor = byId.get(id);
  while (cursor) {
    path.unshift(cursor);
    cursor = cursor.parentId ? byId.get(cursor.parentId) : undefined;
  }
  return path;
}

export interface CertTreeRow {
  node: CertTreeNode;
  level: number;
  hasChildren: boolean;
  expanded: boolean;
  position: number;
  setSize: number;
}

/** Görünür satırlar: açık düğümlerin çocukları (WAI-ARIA tree, `aria-level`). */
export function certificateTreeRows(
  nodes: readonly CertTreeNode[],
  expanded: ReadonlySet<string>,
): CertTreeRow[] {
  const children = new Map<string | null, CertTreeNode[]>();
  for (const node of nodes) {
    const list = children.get(node.parentId) ?? [];
    list.push(node);
    children.set(node.parentId, list);
  }
  const rows: CertTreeRow[] = [];
  const walk = (parent: string | null, level: number) => {
    const list = children.get(parent) ?? [];
    list.forEach((node, index) => {
      const hasChildren = (children.get(node.id) ?? []).length > 0;
      const open = hasChildren && expanded.has(node.id);
      rows.push({
        node,
        level,
        hasChildren,
        expanded: open,
        position: index + 1,
        setSize: list.length,
      });
      if (open) walk(node.id, level + 1);
    });
  };
  walk(null, 1);
  return rows;
}

export type CertTreeAction = {
  focus?: string;
  select?: string;
  toggle?: { id: string; open: boolean };
};

/**
 * WAI-ARIA tree klavyesi (Equipment Tree ile aynı): ↓ ↑ sonraki/önceki satır; → kapalıyı açar,
 * açıkta ilk çocuğa iner; ← açığı kapatır, kapalıda ya da yaprakta üste çıkar; Home/End; Enter ve
 * boşluk seçer.
 */
export function certificateTreeKey(
  key: string,
  rows: readonly CertTreeRow[],
  focusedId: string,
): CertTreeAction | null {
  const index = rows.findIndex((r) => r.node.id === focusedId);
  const row = rows[index];
  if (!row) return null;
  switch (key) {
    case 'ArrowDown': {
      const next = rows[index + 1];
      return next ? { focus: next.node.id } : null;
    }
    case 'ArrowUp': {
      const previous = rows[index - 1];
      return previous ? { focus: previous.node.id } : null;
    }
    case 'ArrowRight': {
      if (!row.hasChildren) return null;
      if (!row.expanded) return { toggle: { id: row.node.id, open: true } };
      const child = rows[index + 1];
      return child ? { focus: child.node.id } : null;
    }
    case 'ArrowLeft': {
      if (row.hasChildren && row.expanded) return { toggle: { id: row.node.id, open: false } };
      for (let i = index - 1; i >= 0; i -= 1) {
        const candidate = rows[i];
        if (candidate && candidate.level < row.level) return { focus: candidate.node.id };
      }
      return null;
    }
    case 'Home': {
      const first = rows[0];
      return first ? { focus: first.node.id } : null;
    }
    case 'End': {
      const last = rows.at(-1);
      return last ? { focus: last.node.id } : null;
    }
    case 'Enter':
    case ' ':
      return { select: row.node.id };
    default:
      return null;
  }
}

/**
 * İlk açılışta açık düğümler (referans): kök, bütün kategoriler ve ilk ara düğüm (SOLAS); seçili
 * düğümün yolu da açık.
 */
export function defaultCertificateExpanded(
  nodes: readonly CertTreeNode[],
  selectedId: string | null,
): Set<string> {
  const open = new Set<string>([ROOT_NODE]);
  for (const node of nodes) if (node.kind === 'category') open.add(node.id);
  const firstGroup = nodes.find((n) => n.kind === 'group');
  if (firstGroup) open.add(firstGroup.id);
  if (selectedId) {
    for (const node of nodePath(nodes, selectedId)) {
      if (node.id !== selectedId || node.kind !== 'type') open.add(node.id);
    }
  }
  return open;
}

/* ---------- Süzgeçler, özet ve sıra ---------- */

export type StatusFilter = 'all' | 'valid' | 'expiring' | 'expired' | 'superseded';
export const STATUS_FILTERS: readonly StatusFilter[] = [
  'all',
  'valid',
  'expiring',
  'expired',
  'superseded',
];
export type DueFilter = 'all' | 'overdue' | '30' | '90' | '365' | 'none';
export const DUE_FILTERS: readonly DueFilter[] = ['all', 'overdue', '30', '90', '365', 'none'];

export interface CertificateFilter {
  /** Ağaç düğümünün türleri; `null` bütün sertifikalar. */
  typeIds: ReadonlySet<string> | null;
  typeId: string | null;
  status: StatusFilter;
  due: DueFilter;
  query: string;
  /** Bitiş tarihi aralığı (üst kart "Date Range"). */
  range: [string | null, string | null];
}

export const EMPTY_FILTER: CertificateFilter = {
  typeIds: null,
  typeId: null,
  status: 'all',
  due: 'all',
  query: '',
  range: [null, null],
};

/** Ağaç düğümünün geçmişte kalmamış sertifikaları (özet kartları ve tür seçeneği). */
export function inScope(
  items: readonly CertificateListItem[],
  typeIds: ReadonlySet<string> | null,
): CertificateListItem[] {
  return items.filter((item) => typeIds === null || typeIds.has(item.typeId));
}

function dueMatches(item: CertificateListItem, due: DueFilter): boolean {
  const left = item.daysLeft;
  switch (due) {
    case 'all':
      return true;
    case 'none':
      return item.expiryDate === null;
    case 'overdue':
      return left !== null && left < 0;
    default:
      return left !== null && left >= 0 && left <= Number(due);
  }
}

/**
 * Listenin süzgeçleri: ağaç düğümü, tür, durum (geçmişte kalan yalnız "Superseded" ile), vade,
 * ad ya da numara araması ve bitiş tarihi aralığı.
 */
export function filterCertificates(
  items: readonly CertificateListItem[],
  filter: CertificateFilter,
): CertificateListItem[] {
  const [from, to] = filter.range;
  return inScope(items, filter.typeIds).filter((item) => {
    if (filter.status === 'superseded') {
      if (!item.superseded) return false;
    } else {
      if (item.superseded) return false;
      if (filter.status !== 'all' && item.status !== filter.status) return false;
    }
    if (filter.typeId !== null && item.typeId !== filter.typeId) return false;
    if (!dueMatches(item, filter.due)) return false;
    if (from !== null || to !== null) {
      if (item.expiryDate === null) return false;
      if (from !== null && item.expiryDate < from) return false;
      if (to !== null && item.expiryDate > to) return false;
    }
    return matchesSearch(`${item.name} ${item.number} ${item.issuedBy}`, filter.query);
  });
}

/**
 * Sertifikanın geçmişi (ortağın önerisi 2026-10-23; ADR 0116 §3): geçmişte kalmamış her
 * sertifikanın yerine geçtiği eski sertifikalar, yeniden eskiye (ISM Form Tree'deki kayıt listesi
 * kalıbı). Eskiler silinmez; güncelin altında "History" olarak görünür. Zincir döngüde de biter.
 */
export function certificateHistory(
  items: readonly CertificateListItem[],
): Map<string, CertificateListItem[]> {
  const byId = new Map(items.map((item) => [item.id, item]));
  const out = new Map<string, CertificateListItem[]>();
  for (const item of items) {
    if (item.superseded) continue;
    const chain: CertificateListItem[] = [];
    const seen = new Set([item.id]);
    let cursor = item.previousId ?? null;
    while (cursor !== null && !seen.has(cursor)) {
      seen.add(cursor);
      const previous = byId.get(cursor);
      if (!previous) break;
      if (previous.superseded) chain.push(previous);
      cursor = previous.previousId ?? null;
    }
    if (chain.length > 0) out.set(item.id, chain);
  }
  return out;
}

/** Özet kartları: Valid, Expiring, Overdue, Total (geçmişte kalmamış sertifikalar). */
export function scopeSummary(items: readonly CertificateListItem[]) {
  const current = items.filter((item) => !item.superseded);
  return {
    valid: current.filter((i) => i.status === 'valid').length,
    expiring: current.filter((i) => i.status === 'expiring').length,
    overdue: current.filter((i) => i.status === 'expired').length,
    total: current.length,
  };
}

export type CertificateSortKey =
  | 'no'
  | 'name'
  | 'type'
  | 'issuedBy'
  | 'issueDate'
  | 'expiryDate'
  | 'status'
  | 'daysLeft';

export interface CertificateSort {
  key: CertificateSortKey;
  dir: 'asc' | 'desc';
}

const STATUS_RANK = { expired: 0, expiring: 1, valid: 2 } as const;

/**
 * Sıra: varsayılan ("No") ağaç sırası (tür) ve ad; sütunlar iki yönde. Boş tarih ve süresiz belge
 * her yönde sonda.
 */
export function sortCertificates(
  items: readonly CertificateListItem[],
  sort: CertificateSort,
  typeOrder: ReadonlyMap<string, number>,
  locale: string,
): CertificateListItem[] {
  const text = (a: string, b: string) => a.localeCompare(b, locale, { sensitivity: 'base' });
  const order = (item: CertificateListItem) =>
    typeOrder.get(item.typeId) ?? Number.MAX_SAFE_INTEGER;
  const base = (a: CertificateListItem, b: CertificateListItem) =>
    order(a) - order(b) || text(a.name, b.name) || text(a.number, b.number);
  const sign = sort.dir === 'asc' ? 1 : -1;
  const nullable = <T>(
    a: T | null,
    b: T | null,
    compare: (x: T, y: T) => number,
  ): number | null => {
    if (a === null && b === null) return 0;
    if (a === null) return Number.POSITIVE_INFINITY;
    if (b === null) return Number.NEGATIVE_INFINITY;
    return compare(a, b) * sign;
  };
  const compare = (a: CertificateListItem, b: CertificateListItem): number => {
    switch (sort.key) {
      case 'no':
        return base(a, b) * sign;
      case 'name':
        return text(a.name, b.name) * sign || base(a, b);
      case 'type':
        return text(a.type?.name ?? '', b.type?.name ?? '') * sign || base(a, b);
      case 'issuedBy':
        return text(a.issuedBy, b.issuedBy) * sign || base(a, b);
      case 'issueDate':
        return text(a.issueDate, b.issueDate) * sign || base(a, b);
      case 'expiryDate': {
        const value = nullable(a.expiryDate, b.expiryDate, text);
        return value === null || value === 0 ? base(a, b) : Math.sign(value);
      }
      case 'status':
        return (STATUS_RANK[a.status] - STATUS_RANK[b.status]) * sign || base(a, b);
      case 'daysLeft': {
        const value = nullable(a.daysLeft, b.daysLeft, (x, y) => x - y);
        return value === null || value === 0 ? base(a, b) : Math.sign(value);
      }
    }
  };
  return [...items].sort(compare);
}

/* ---------- Adres sorgusu ---------- */

export const CERTIFICATE_VIEWS = ['list', 'surveys', 'regulatory', 'calendar'] as const;
export type CertificateView = (typeof CERTIFICATE_VIEWS)[number];

export interface CertificateQuery {
  vessel: string | null;
  node: string | null;
  cert: string | null;
  view: CertificateView;
}

/** Adres: web'de seçili gemi `?vessel=`; ağaç `?node=`, ayrıntı `?cert=`, görünüm `?view=`. */
export function readCertificateQuery(search: string | undefined): CertificateQuery {
  const params = new URLSearchParams(search ?? '');
  const view = params.get('view');
  return {
    vessel: params.get('vessel'),
    node: params.get('node'),
    cert: params.get('cert'),
    view: (CERTIFICATE_VIEWS as readonly string[]).includes(view ?? '')
      ? (view as CertificateView)
      : 'list',
  };
}

/** Sorguyu günceller; gemi değişince ağaç seçimi ve ayrıntı düşer (başka geminin kaydı olamaz). */
export function writeCertificateQuery(
  search: string | undefined,
  patch: Partial<CertificateQuery>,
): string {
  const current = readCertificateQuery(search);
  const vesselChanged = patch.vessel !== undefined && patch.vessel !== current.vessel;
  const next: CertificateQuery = {
    vessel: patch.vessel !== undefined ? patch.vessel : current.vessel,
    node: patch.node !== undefined ? patch.node : vesselChanged ? null : current.node,
    cert: patch.cert !== undefined ? patch.cert : vesselChanged ? null : current.cert,
    view: patch.view ?? current.view,
  };
  const params = new URLSearchParams();
  if (next.vessel) params.set('vessel', next.vessel);
  if (next.node) params.set('node', next.node);
  if (next.cert) params.set('cert', next.cert);
  if (next.view !== 'list') params.set('view', next.view);
  const text = params.toString();
  return text ? `?${text}` : '';
}

/* ---------- Alt paneller ---------- */

export type UpcomingTone = 'expiring' | 'expired' | 'upcoming' | 'open' | 'overdue';

export interface UpcomingRow {
  key: string;
  item: CertificateListItem;
  dueOn: string;
  /** `expiry` ya da sörvey penceresinin türü. */
  kind: string;
  daysLeft: number;
  tone: UpcomingTone;
}

/** "Upcoming Certificates": sunucunun 90 günlük listesi (K30), liste maddesiyle birleşik. */
export function upcomingRows(list: CertificateList, today: string): UpcomingRow[] {
  const byId = new Map(list.items.map((i) => [i.id, i]));
  return list.upcoming.flatMap((due) => {
    const item = byId.get(due.id);
    if (!item) return [];
    const tone: UpcomingTone =
      due.kind === 'expiry'
        ? item.status === 'valid'
          ? 'upcoming'
          : item.status
        : (item.nextSurvey?.state ?? 'upcoming');
    return [
      {
        key: `${due.id}:${due.kind}:${due.dueOn}`,
        item,
        dueOn: due.dueOn,
        kind: due.kind,
        daysLeft: daysBetween(today, due.dueOn),
        tone,
      },
    ];
  });
}

/** "Certificate Status Distribution": sayılar ve yüzdeler (en büyük kalan; toplam 100). */
export function distribution(summary: {
  valid: number;
  expiring: number;
  overdue: number;
  total: number;
}): { key: 'valid' | 'expiring' | 'overdue'; value: number; percent: number }[] {
  const parts = [
    { key: 'valid' as const, value: summary.valid },
    { key: 'expiring' as const, value: summary.expiring },
    { key: 'overdue' as const, value: summary.overdue },
  ];
  const sum = parts.reduce((acc, p) => acc + p.value, 0);
  if (sum === 0) return parts.map((p) => ({ ...p, percent: 0 }));
  const raw = parts.map((p) => (p.value / sum) * 100);
  const floors = raw.map(Math.floor);
  let rest = 100 - floors.reduce((acc, v) => acc + v, 0);
  const order = raw
    .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
    .sort((a, b) => b.fraction - a.fraction);
  for (const { index } of order) {
    if (rest <= 0) break;
    floors[index] = (floors[index] ?? 0) + 1;
    rest -= 1;
  }
  return parts.map((p, i) => ({ ...p, percent: floors[i] ?? 0 }));
}

/** Survey Schedule'ın sabit satırları (PMS belgesi §19). */
export const SCHEDULE_KINDS: readonly SurveyKind[] = [
  'annual',
  'intermediate',
  'special',
  'bottom',
  'boiler',
  'safety_equipment',
  'radio',
];

/** Survey Schedule: §19 satırları sırayla (kaydı yoksa boş), ek türler ve ikinci kayıtlar sonda. */
export function surveyScheduleRows(
  surveys: readonly VesselSurvey[],
): { kind: SurveyKind; survey: VesselSurvey | null }[] {
  const used = new Set<string>();
  const rows: { kind: SurveyKind; survey: VesselSurvey | null }[] = SCHEDULE_KINDS.map((kind) => {
    const survey = surveys.find((s) => s.kind === kind && !used.has(s.id)) ?? null;
    if (survey) used.add(survey.id);
    return { kind, survey };
  });
  const rest = surveys
    .filter((s) => !used.has(s.id))
    .sort(
      (a, b) =>
        SURVEY_KINDS.indexOf(a.kind) - SURVEY_KINDS.indexOf(b.kind) ||
        (a.nextDueOn ?? '9999').localeCompare(b.nextDueOn ?? '9999'),
    );
  return [...rows, ...rest.map((survey) => ({ kind: survey.kind, survey }))];
}

/** Ayın haftaları (pazartesiden); ay dışındaki günler `null`. */
export function monthGrid(month: string): (string | null)[][] {
  const first = `${month}-01`;
  const days: string[] = [];
  for (let day = first; day.slice(0, 7) === month; day = addDays(day, 1)) days.push(day);
  // 1970-01-05 pazartesidir
  const weekday = (day: string) => (((daysBetween('1970-01-05', day) % 7) + 7) % 7) as number;
  const cells: (string | null)[] = [...Array.from({ length: weekday(first) }, () => null), ...days];
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks: (string | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

export interface CalendarEvent {
  day: string;
  kind: 'expiry' | 'survey';
  item: CertificateListItem;
}

/** Certificate Calendar: ayın bitiş günleri ve sıradaki sörvey vadeleri (geçmişte kalmamış). */
export function calendarEvents(
  items: readonly CertificateListItem[],
  month: string,
): CalendarEvent[] {
  const events: CalendarEvent[] = [];
  for (const item of items) {
    if (item.superseded) continue;
    if (item.expiryDate?.startsWith(month)) {
      events.push({ day: item.expiryDate, kind: 'expiry', item });
    }
    if (item.nextSurvey?.due.startsWith(month)) {
      events.push({ day: item.nextSurvey.due, kind: 'survey', item });
    }
  }
  return events.sort(
    (a, b) => a.day.localeCompare(b.day) || a.item.name.localeCompare(b.item.name, 'en'),
  );
}

/* ---------- Notlar ve eylemler ---------- */

const REMARK = /^(\d{4}-\d{2}-\d{2}) (.+?): (.*)$/;

/**
 * "Add Remark" satırları (sunucu: `YYYY-MM-DD Ad Soyad: metin`; ADR 0029 D13). Kalıba uymayan
 * satır önceki notun devamıdır; ilk satırsa tarihsiz not.
 */
export function remarkLines(
  remarks: string | null,
): { date: string | null; person: string | null; text: string }[] {
  if (!remarks) return [];
  const lines: { date: string | null; person: string | null; text: string }[] = [];
  for (const line of remarks.split('\n')) {
    const match = REMARK.exec(line);
    const last = lines.at(-1);
    if (match)
      lines.push({ date: match[1] ?? null, person: match[2] ?? null, text: match[3] ?? '' });
    else if (last) last.text = `${last.text}\n${line}`;
    else lines.push({ date: null, person: null, text: line });
  }
  return lines;
}

export interface CertificateActionSet {
  /** "+ Add Certificate", Quick Links "Add New Certificate". */
  add: boolean;
  /** Satırdaki düzenleme ikonu, çekmece. */
  edit: boolean;
  remove: boolean;
  /** "Add Remark" (ofis). */
  remark: boolean;
  /** Sörvey, CoC, yetki beyanı ve kalem listesi formları (ofis). */
  office: boolean;
  /** Gemide düzenlenen sertifikanın ofis onayı (ADR 0116 §2): sertifika ekleme yetkisiyle. */
  approve: boolean;
  /** "Record Endorsement" (gemi). */
  endorse: boolean;
  /** "Record Surveyor Confirmation" (gemi). */
  confirm: boolean;
  /** "Record On-board Issuance" (gemi). */
  issue: boolean;
}

/**
 * Eylem × uygulama (plan M7 "Eylem × uygulama"; K28): ofis formları yalnız web'de ve yetkiye göre;
 * vize, sörveyör teyidi ve gemide düzenlenen sertifika yalnız gemide.
 */
export function certificateActions(
  scope: 'web' | 'ship',
  can: CertificateCan | undefined,
): CertificateActionSet {
  const web = scope === 'web';
  const ship = scope === 'ship' && can?.record === true;
  return {
    add: web && can?.create === true,
    edit: web && can?.edit === true,
    remove: web && can?.void === true,
    remark: web && can?.edit === true,
    office: web && (can?.create === true || can?.edit === true),
    approve: web && can?.create === true,
    endorse: ship,
    confirm: ship,
    issue: ship,
  };
}

/* ---------- Ekranın küçük yardımcıları ---------- */

/**
 * Liste kartının başlığındaki kapsam ("Certificate List – SOLAS / Safety Equipment"): kökte
 * `null` (bütün sertifikalar), kategoride kategori adı, ara düğüm ve türde kategoriden sonraki yol.
 */
export function listScope(
  nodes: readonly CertTreeNode[],
  id: string,
  label: (node: CertTreeNode) => string,
): string | null {
  const path = nodePath(nodes, id);
  const last = path.at(-1);
  if (!last || last.kind === 'root') return null;
  if (last.kind === 'category') return label(last);
  return path
    .filter((n) => n.kind === 'group' || n.kind === 'type')
    .map(label)
    .join(' / ');
}

/** "Export" süzgeci (ADR 0029 Ek: M7e1 R1): seçili düğümün kategorisi; kökte bütün gemi. */
export function exportCategory(nodes: readonly CertTreeNode[], id: string): string | undefined {
  const node = nodes.find((n) => n.id === id);
  return node && node.kind !== 'root' ? (node.category ?? undefined) : undefined;
}

/** Sayfa: süzgeç daralınca sayfa son sayfaya çekilir; "Showing 1 – 15 of 24". */
export function pageOf<T>(rows: readonly T[], page: number, size: number) {
  const total = rows.length;
  const pages = Math.max(1, Math.ceil(total / size));
  const current = Math.min(Math.max(1, page), pages);
  const start = (current - 1) * size;
  const slice = rows.slice(start, start + size);
  return {
    rows: slice,
    from: total === 0 ? 0 : start + 1,
    to: start + slice.length,
    total,
    pages,
    page: current,
  };
}

/** Hatırlatma kademeleri (K30; ADR 0029 D7): bitişten 90, 60 ve 30 gün önce; süresizde yok. */
export function reminderStages(daysLeft: number | null): { days: number; reached: boolean }[] {
  if (daysLeft === null) return [];
  return [90, 60, 30].map((days) => ({ days, reached: daysLeft <= days }));
}

/**
 * Sörveyör teyidine açık kalemler (K31; ADR 0029 D10): klas için sayılmış ve henüz teyit
 * edilmemiş başmühendis sörveyi olanlar.
 */
export function confirmableItems(rows: readonly ClassDueRow[]): ClassDueRow[] {
  return rows.filter(
    (row) =>
      row.state === 'awaiting_confirmation' &&
      row.lastCeSurvey !== null &&
      row.lastCeSurvey.ceCredit &&
      row.confirmation === null,
  );
}

/** Gemide düzenlenen sertifikanın yerine geçebileceği: aynı türün geçmişte kalmamış ofis kaydı. */
export function replaceable(
  items: readonly CertificateListItem[],
  typeId: string | null,
): CertificateListItem[] {
  if (!typeId) return [];
  return items.filter((i) => i.typeId === typeId && i.source === 'office' && !i.superseded);
}
